import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { m } from 'framer-motion'

// Components
import RDPHeader from '../Components/Header/RDPHeader'
import RDPFooter from '../Components/Footers/RDPFooter'

// Context
import { useCart } from '../contexts/CartContext'

// Supabase
import { supabase } from '../lib/supabaseClient'

// Mapping couleurs vers codes hex
const COLOR_HEX = {
  'Blanc': '#FFFFFF',
  'Antique': '#8B7355',
  'Brun': '#654321',
  'Vert': '#228B22',
  'Bleu': '#4169E1',
  'Noir': '#1a1a1a',
  'Transparent': '#E8E8E8'
}

// Slugify pour l'URL de la gamme
const slugify = (str) =>
  str
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')

const ProductDetailPage = (props) => {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { addToCart, removeFromCart, isInCart } = useCart()

  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [addedToCart, setAddedToCart] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)

  useEffect(() => {
    window.scrollTo(0, 0)
    fetchProduct()
  }, [slug])

  const fetchProduct = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('slug', slug)
        .single()

      if (error) throw error
      setProduct(data)
    } catch (error) {
      console.error('Erreur lors du chargement du produit:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCartToggle = () => {
    if (product) {
      if (inCart) {
        removeFromCart(product.id)
      } else {
        const success = addToCart(product)
        if (success) {
          setAddedToCart(true)
          setTimeout(() => setAddedToCart(false), 1000)
        }
      }
    }
  }

  if (loading) {
    return (
      <div style={props.style}>
        <RDPHeader {...props} />
        <section className="pt-[130px] pb-[80px] lg:pt-[110px] lg:pb-[60px] md:pt-[100px] md:pb-[50px]">
          <Container>
            <div className="text-center py-20">
              <div style={{
                display: 'inline-block',
                width: '40px',
                height: '40px',
                border: '4px solid #E5E7EB',
                borderTop: '4px solid #3B82F6',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p className="text-lg text-glass-dark mt-4">Chargement du produit...</p>
            </div>
          </Container>
        </section>
        <RDPFooter />
      </div>
    )
  }

  if (!product) {
    return (
      <div style={props.style}>
        <RDPHeader {...props} />
        <section className="pt-[130px] pb-[80px] lg:pt-[110px] lg:pb-[60px] md:pt-[100px] md:pb-[50px]">
          <Container>
            <div className="text-center py-20">
              <h2 className="text-glass-darkest mb-4">Produit non trouvé</h2>
              <Button
                onClick={() => navigate('/produits')}
                className="glass-btn-medium"
              >
                Retour aux produits
              </Button>
            </div>
          </Container>
        </section>
        <RDPFooter />
      </div>
    )
  }

  const inCart = isInCart(product.id)

  // Gestion des images par couleur
  const colors = product.colors || []
  const colorImages = product.color_images || {}
  const defaultColor = product.default_color
  const hasMultipleColors = colors.length > 1 && Object.keys(colorImages).length > 0

  // Image à afficher : couleur sélectionnée > couleur par défaut > image principale
  const getDisplayImage = () => {
    if (selectedColor && colorImages[selectedColor]) {
      return colorImages[selectedColor]
    }
    // Si pas de sélection, utiliser la couleur par défaut
    if (defaultColor && colorImages[defaultColor]) {
      return colorImages[defaultColor]
    }
    return product.image_url || 'https://placehold.co/600x550/E5E7EB/9CA3AF?text=Aucune+image'
  }

  // Déterminer la couleur active (pour le highlight)
  const activeColor = selectedColor || defaultColor

  return (
    <div style={props.style}>
      <RDPHeader {...props} />

      {/* Compact Product Section */}
      <section className="pt-[110px] pb-[50px] lg:pt-[100px] lg:pb-[40px] md:pt-[90px] md:pb-[30px] sm:pt-[100px] min-h-[calc(100vh-80px)] flex items-center">
        <Container fluid="lg" className="w-full">
          {/* Bouton retour */}
          <Row className="mb-4">
            <Col>
              <button
                onClick={() => navigate(-1)}
                className="text-glass-accent hover:text-glass-darkest flex items-center gap-2 transition-colors text-sm font-medium"
              >
                <i className="line-icon-Arrow-Left text-lg"></i>
                Retour
              </button>
            </Col>
          </Row>

          {/* Contenu principal */}
          <Row className="items-stretch">
            {/* Colonne gauche - Image et badge */}
            <Col lg={5} xl={4} className="mb-4 lg:mb-0">
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="h-full"
              >
                <div className="bg-gradient-to-br from-glass-offWhite to-white rounded-[24px] p-8 shadow-xl h-full flex flex-col">
                  {/* Image */}
                  <div className="flex-1 flex items-center justify-center">
                    <img
                      src={getDisplayImage()}
                      alt={product.name}
                      className="w-full h-auto object-contain transition-all duration-300"
                      style={{ maxHeight: '450px' }}
                    />
                  </div>

                  {/* Sélecteur de couleur sous l'image - parallélogrammes */}
                  {hasMultipleColors && (
                    <div className="mt-6 pt-4 border-t border-glass-border">
                      <p className="text-sm text-glass-medium mb-3 font-medium">Couleurs disponibles :</p>
                      <div className="flex gap-2 justify-center flex-wrap">
                        {colors.map((color) => {
                          if (!colorImages[color]) return null
                          const isActive = activeColor === color
                          return (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(color)}
                              className={`flex flex-col items-center gap-2 p-2 rounded-lg transition-all duration-200 ${
                                isActive ? 'bg-glass-pearl shadow-md' : 'hover:bg-glass-offWhite'
                              }`}
                              title={color}
                            >
                              <span
                                className={`w-[32px] h-[42px] shadow-md transition-all duration-200 ${
                                  isActive ? 'ring-2 ring-glass-accent scale-110' : 'hover:scale-105'
                                }`}
                                style={{
                                  backgroundColor: COLOR_HEX[color] || '#ccc',
                                  border: color === 'Blanc' || color === 'Transparent' ? '2px solid #ddd' : 'none',
                                  transform: `skewX(-12deg)${isActive ? ' scale(1.1)' : ''}`,
                                  borderRadius: '4px'
                                }}
                              />
                              <span className={`text-xs font-medium ${isActive ? 'text-glass-accent' : 'text-glass-medium'}`}>
                                {color}
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </m.div>
            </Col>

            {/* Colonne droite - Informations */}
            <Col lg={7} xl={8}>
              <m.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="h-full"
              >
                {/* En-tête produit */}
                <div className="mb-6">
                  <h1 className="font-serif font-bold text-glass-darkest text-[2.75rem] xl:text-[3rem] lg:text-[2.5rem] md:text-[2rem] mb-3 leading-tight">
                    {product.name}
                  </h1>

                  {product.code && (
                    <span className="inline-block px-4 py-2 bg-glass-pearl text-glass-dark rounded-lg text-sm font-mono font-bold">
                      Code: {product.code}
                    </span>
                  )}
                </div>

                {/* Actions principales */}
                <div className="flex flex-wrap gap-3 mb-6 pb-6 border-b-2 border-glass-border">
                  <button
                    onClick={handleCartToggle}
                    className={`${
                      addedToCart
                        ? 'bg-white text-green-600 hover:scale-[1.02] hover:-translate-y-0.5'
                        : inCart
                        ? 'bg-red-50 text-red-600 hover:bg-red-100 hover:scale-[1.02] hover:-translate-y-0.5'
                        : 'bg-white text-glass-darkest hover:scale-[1.02] hover:-translate-y-0.5'
                    } px-8 py-3 rounded-full font-bold transition-all duration-200 flex items-center gap-3 text-base shadow-lg hover:shadow-xl`}
                  >
                    <i className={`text-xl ${addedToCart ? 'line-icon-Check' : inCart ? 'line-icon-Close' : 'line-icon-Add-Bag'}`}></i>
                    {addedToCart ? 'Ajouté !' : inCart ? 'Retirer de ma demande' : 'Ajouter à ma demande'}
                  </button>

                  {product.pdf_url && (
                    <a
                      href={product.pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="bg-white text-glass-darkest hover:scale-[1.02] hover:-translate-y-0.5 px-8 py-3 rounded-full font-bold transition-all duration-200 flex items-center gap-3 text-base shadow-lg hover:shadow-xl"
                      style={{ textDecoration: 'none' }}
                    >
                      <i className="line-icon-File-Document text-xl"></i>
                      Plan technique
                    </a>
                  )}

                  {product.range && (
                    <Link
                      to={`/gamme/${slugify(product.range)}`}
                      className="bg-white text-glass-darkest hover:scale-[1.02] hover:-translate-y-0.5 px-8 py-3 rounded-full font-bold transition-all duration-200 flex items-center gap-3 text-base shadow-lg hover:shadow-xl"
                    >
                      <i className="line-icon-Box-Open text-xl"></i>
                      Voir la gamme
                    </Link>
                  )}
                </div>

                {/* Grille d'informations compacte */}
                <Row className="g-3">
                  {/* Caractéristiques principales */}
                  {(product.capacity || product.colors?.length > 0 || product.color || product.cap || product.type) && (
                    <Col lg={6}>
                      <div className="bg-gradient-to-br from-glass-pearl to-glass-offWhite rounded-[16px] p-5 h-full">
                        <h3 className="font-serif font-bold text-glass-darkest text-lg mb-4 flex items-center gap-2">
                          <i className="line-icon-Flask text-glass-accent text-xl"></i>
                          Caractéristiques
                        </h3>
                        <div className="space-y-3">
                          {product.capacity && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Capacité ras bord</span>
                              <span className="text-glass-darkest text-base font-bold">{product.capacity} ml</span>
                            </div>
                          )}
                          {(product.colors?.length > 0 || product.color) && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">
                                {product.colors?.length > 1 ? 'Couleurs' : 'Couleur'}
                              </span>
                              <span className="text-glass-darkest text-base font-bold">
                                {product.colors?.length > 0
                                  ? product.colors.join(', ')
                                  : product.color}
                              </span>
                            </div>
                          )}
                          {product.cap && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Bouchage</span>
                              <span className="text-glass-darkest text-base font-bold">{product.cap}</span>
                            </div>
                          )}
                          {product.type && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Type</span>
                              <span className="text-glass-darkest text-base font-bold">{product.type}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  )}

                  {/* Spécifications techniques */}
                  {(product.weight || product.height || product.diameter_shoulder || product.diameter_base || product.units_per_pallet) && (
                    <Col lg={6}>
                      <div className="bg-gradient-to-br from-glass-pearl to-glass-offWhite rounded-[16px] p-5 h-full">
                        <h3 className="font-serif font-bold text-glass-darkest text-lg mb-4 flex items-center gap-2">
                          <i className="line-icon-Ruler text-glass-accent text-xl"></i>
                          Spécifications
                        </h3>
                        <div className="space-y-3">
                          {product.weight && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Poids</span>
                              <span className="text-glass-darkest text-base font-bold">{product.weight} g</span>
                            </div>
                          )}
                          {product.height && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Hauteur</span>
                              <span className="text-glass-darkest text-base font-bold">{product.height} mm</span>
                            </div>
                          )}
                          {product.diameter_shoulder && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Ø épaule</span>
                              <span className="text-glass-darkest text-base font-bold">{product.diameter_shoulder}</span>
                            </div>
                          )}
                          {product.diameter_base && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Ø base</span>
                              <span className="text-glass-darkest text-base font-bold">{product.diameter_base}</span>
                            </div>
                          )}
                          {product.units_per_pallet && (
                            <div className="flex justify-between items-center">
                              <span className="text-glass-medium text-sm font-semibold">Unités/palette</span>
                              <span className="text-glass-darkest text-base font-bold">{product.units_per_pallet}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>

              </m.div>
            </Col>
          </Row>
        </Container>
      </section>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      <RDPFooter />
    </div>
  )
}

export default ProductDetailPage
